/** Reine Logik von Schritt 2 „Kandidaten“: Orte, Kriterien-Chips, Blacklist-Eingaben. */

import { DEFAULT_RADIUS, nextRadius, profileLabel, sameName } from '../../campaign-wizard.logic';
import {
  GeoOption,
  Radius,
  WizardBlacklistList,
  WizardLocation,
  WizardState,
} from '../../campaign-wizard.types';

export { DEFAULT_RADIUS };

/** Radius der Stufe weiter (Zyklus über die acht LinkedIn-Stufen); die Oberfläche wählt per Menü, der Zyklus bleibt für Tests/Tastatur. */
export const cycleLocationRadius = (locations: WizardLocation[], index: number): WizardLocation[] =>
  locations.map((l, i) => (i === index ? { ...l, radius: nextRadius(l.radius ?? DEFAULT_RADIUS) } : l));

export const removeLocation = (locations: WizardLocation[], index: number): WizardLocation[] =>
  locations.filter((_, i) => i !== index);

/**
 * Fügt einen Ort-VORSCHLAG (z. B. aus dem Briefing oder der Ortsfrage aus Schritt 1) hinzu: ohne LinkedIn-ID, mit dem
 * Standard-Radius. Die Oberfläche markiert ihn „bitte bestätigen“; leere Eingabe und doppelte Orte ändern nichts.
 */
export const addLocation = (locations: WizardLocation[], text: string): WizardLocation[] => {
  const city = text.trim();
  if (!city || locations.some(l => sameName(l.city, city))) return locations;
  return [...locations, { city, radius: DEFAULT_RADIUS }];
};

/** Setzt den Radius des Ortes auf eine Stufe oder `null` (= ganze Region) (Auswahl im Menü). */
export const setLocationRadius = (
  locations: WizardLocation[],
  index: number,
  radius: Radius | null
): WizardLocation[] => locations.map((l, i) => (i === index ? { ...l, radius } : l));

/** Anzeige „+ 40 km“ bzw. „+ 1,6 km“ (Dezimalzeichen nach Sprache). */
export const radiusLabel = (radius: number, locale = 'de'): string =>
  `+ ${radius.toLocaleString(locale)} km`;

/* ---- Orte aus der LinkedIn-Ortssuche ------------------------------------------------------------------------- */

/** Vorschläge erst ab zwei Zeichen (nach dem Trimmen). */
export const GEO_MIN_QUERY = 2;
/** Höchstens so viele Vorschläge in der Liste. */
export const GEO_MAX_OPTIONS = 8;
/** Wartezeit nach dem letzten Tastendruck, bevor LinkedIn gefragt wird. */
export const GEO_DEBOUNCE_MS = 350;

export const geoQueryReady = (query: string): boolean => query.trim().length >= GEO_MIN_QUERY;

const REGION_WORDS = /^(gro(ß|ss)raum|regierungsbezirk|metropolregion|region|bezirk|kreis|landkreis|greater|metro|county)\b|umgebung|\barea\b/i;

/**
 * Region statt Stadt? Die Ortssuche liefert nur den Anzeigetext („Köln, Nordrhein-Westfalen, Deutschland“ = Stadt,
 * „Nordrhein-Westfalen, Deutschland“ = Bundesland, „Deutschland“ = Land, „Großraum Köln/Bonn“ = Market Area), keinen Typ.
 * Daher: höchstens zwei Teile oder ein typisches Regionswort = Region. Für Regionen gilt „Ganze Region“, nie ein Radius.
 */
export const isRegionText = (text: string): boolean => {
  const parts = text.split(',').map(p => p.trim()).filter(Boolean);
  if (parts.length <= 2) return true;
  return REGION_WORDS.test(parts[0]);
};

/** Antwort von `POST /linkedin/lookups/geo` (`[[id, text], …]`) → Vorschläge: nur gültige Paare, ohne Doppelte, höchstens acht. */
export const parseGeoResponse = (data: unknown): GeoOption[] => {
  if (!Array.isArray(data)) return [];
  const seen = new Set<string>();
  const options: GeoOption[] = [];
  for (const entry of data) {
    const id = Array.isArray(entry) ? String(entry[0] ?? '').trim() : '';
    const text = Array.isArray(entry) ? String(entry[1] ?? '').trim() : '';
    if (!id || !text || seen.has(id)) continue;
    seen.add(id);
    options.push({ id, text, region: isRegionText(text) });
    if (options.length >= GEO_MAX_OPTIONS) break;
  }
  return options;
};

export type GeoErrorKind = 'session' | 'failed';

/** Abgelaufene LinkedIn-Sitzung (409 / 400 mit `SESSION_INVALID`) vs. sonstiger Fehler. */
export const geoErrorKind = (error: { status?: number; code?: string } | null | undefined): GeoErrorKind =>
  error?.code === 'SESSION_INVALID' || error?.status === 409 ? 'session' : 'failed';

/** Der Ort wurde nicht aus der LinkedIn-Ortssuche gewählt (altes Entwurf, Briefing, Ortsfrage) → „bitte bestätigen“. */
export const isUnconfirmed = (location: WizardLocation): boolean => !location.geoUrn;

/**
 * Kann die Kontoart in der Wizard-Suche einen Radius? Nur Sales Navigator (POSTAL_CODE + Umkreis). Recruiter braucht für den
 * Radius eine echte PLZ, die normale LinkedIn-Suche kennt keinen Radius. Muss zum Backend passen (`supportsRadius`).
 */
export const supportsRadius = (productType: string | null | undefined): boolean =>
  productType === 'sales_navigator';

/**
 * Kann die Kontoart „PLZ + Umkreis“ (Umschalter in Schritt 2)? Sales Navigator (POSTAL_CODE + Umkreis) und Recruiter
 * (PLZ + Entfernung in km). Basic/Premium nicht. Muss zum Backend passen (`supportsPostalRadius`).
 */
export const supportsPostalRadius = (productType: string | null | undefined): boolean =>
  productType === 'sales_navigator' || productType === 'recruiter';

/** Ist der Ort ein „PLZ + Umkreis“-Eintrag? */
export const isPostalLocation = (location: WizardLocation): boolean => !!location.postalCode;

/** Chip-Text eines Ortes: bei PLZ-Einträgen die PLZ, sonst der Ortsname. */
export const locationLabel = (location: WizardLocation): string => location.postalCode || location.city;

const ZIP = /\b(\d{4,5})\b/;

/** Erste 4–5-stellige Zahl eines Textes („80331, München, …“ → „80331“). */
export const zipOf = (text: string): string | null => ZIP.exec(text)?.[1] ?? null;

/** `urn:li:ts_geo:123` / `urn:li:geo:123` / `123` → `123` (das Backend sucht mit der reinen Zahl). */
export const plainGeoId = (raw: string): string => raw.trim().replace(/^urn:li:[a-z_]*geo:/i, '');

/**
 * Antwort der PLZ-Suche → Vorschläge. Zwei Formen: Sales Navigator `POST /linkedin/lookups/geo/postal-code`
 * (`{ locations: [{ id, text }] }`), Recruiter `POST /linkedin/lookups/recruiter-constraints?searchType=zip`
 * (`{ values: [{ entityUrn, text }] }`). Recruiter braucht eine echte PLZ im Text; Treffer ohne PLZ fallen dort weg.
 */
export const parsePostalResponse = (data: unknown, productType?: string | null): GeoOption[] => {
  const body = (data && typeof data === 'object' ? data : {}) as {
    locations?: Array<{ id?: unknown; text?: unknown }>;
    values?: Array<{ entityUrn?: unknown; text?: unknown }>;
  };
  const rows = Array.isArray(body.locations)
    ? body.locations.map(l => ({ id: l?.id, text: l?.text }))
    : Array.isArray(body.values)
      ? body.values.map(v => ({ id: v?.entityUrn, text: v?.text }))
      : [];
  const seen = new Set<string>();
  const options: GeoOption[] = [];
  for (const row of rows) {
    const id = plainGeoId(String(row.id ?? ''));
    const text = String(row.text ?? '').trim();
    if (!id || !text || seen.has(id)) continue;
    if (productType === 'recruiter' && !zipOf(text)) continue;
    seen.add(id);
    options.push({ id, text, region: false });
    if (options.length >= GEO_MAX_OPTIONS) break;
  }
  return options;
};

/**
 * Wählt der Nutzer einen PLZ-Treffer, entsteht ein „PLZ + Umkreis“-Ort mit dem Standard-Radius. Die PLZ steht im Treffer
 * („80331, München …“), sonst in der Eingabe, sonst ist es der Ortsname (Sales Navigator liefert auch Orte).
 */
export const locationFromPostalOption = (option: GeoOption, query = ''): WizardLocation => ({
  city: option.text,
  radius: DEFAULT_RADIUS,
  geoUrn: option.id,
  postalCode: zipOf(option.text) ?? zipOf(query) ?? (option.text.split(',')[0].trim() || option.text),
});

/** Hängt einen PLZ-Ort an; dieselbe PLZ mit derselben ID gibt es nur einmal. */
export const addPostalOption = (
  locations: WizardLocation[],
  option: GeoOption,
  query = ''
): WizardLocation[] => {
  const next = locationFromPostalOption(option, query);
  if (locations.some(l => l.geoUrn === next.geoUrn && l.postalCode === next.postalCode)) return locations;
  return [...locations, next];
};

/** Startwert für die Suche beim Bestätigen: nur der Ortsname („Köln, Nordrhein-Westfalen“ → „Köln“). */
export const searchTextOf = (city: string): string => city.split(',')[0].trim() || city.trim();

/**
 * Radius, der tatsächlich gesucht wird: nur bestätigte Städte (mit LinkedIn-ID) bei Konten mit Radius-Suche
 * (Sales Navigator); alles andere „ganze Region“ (`null`). Gilt in Schritt 2, in der Suche und in der Zusammenfassung.
 */
export const effectiveRadius = (
  location: WizardLocation,
  radiusSupported: boolean,
  postalSupported = false
): Radius | null => {
  // „PLZ + Umkreis“ hat immer einen Radius, sofern das Konto es kann (sonst wird der Ort als Ort gesucht).
  if (isPostalLocation(location)) return postalSupported ? (location.radius ?? DEFAULT_RADIUS) : null;
  return radiusSupported && !location.region && !isUnconfirmed(location) ? location.radius : null;
};

/** Kann der Radius des Ortes geändert werden? Nur Städte bei Konten mit Radius-Suche. */
export const canEditRadius = (
  location: WizardLocation,
  radiusSupported: boolean,
  postalSupported = false
): boolean => (isPostalLocation(location) ? postalSupported : radiusSupported && !location.region);

/**
 * Wählt der Nutzer einen Vorschlag, entsteht daraus der gespeicherte Ort. Radius: Stadt + Konto mit Radius-Suche = der
 * bisherige Radius des Ortes (sonst 40 km); Region oder Konto ohne Radius = ganze Region (`null`).
 */
export const locationFromOption = (
  option: GeoOption,
  radiusSupported: boolean,
  previous?: WizardLocation
): WizardLocation => ({
  city: option.text,
  radius: radiusSupported && !option.region ? (previous ? previous.radius : DEFAULT_RADIUS) : null,
  geoUrn: option.id,
  ...(option.region ? { region: true } : {}),
});

/**
 * Übernimmt einen gewählten Ort: `index` ersetzt einen Vorschlag („bitte bestätigen“), `null` hängt an. Ein Ort mit
 * derselben LinkedIn-ID gibt es nur einmal; die Reihenfolge der übrigen bleibt.
 */
export const applyLocationOption = (
  locations: WizardLocation[],
  index: number | null,
  option: GeoOption,
  radiusSupported: boolean
): WizardLocation[] => {
  const next = locationFromOption(option, radiusSupported, index === null ? undefined : locations[index]);
  const duplicate = (l: WizardLocation, i: number) => i !== index && l.geoUrn === option.id;
  const base = locations.filter((l, i) => !duplicate(l, i));
  const at = index === null ? -1 : locations.slice(0, index).filter((l, i) => !duplicate(l, i)).length;
  if (index === null || index >= locations.length) return [...base, next];
  return base.map((l, i) => (i === at ? next : l));
};

/** Fügt einen Chip-Text hinzu (getrimmt, ohne Duplikate, ohne Leerwerte). */
export const addChipValue = (values: string[], text: string): string[] => {
  const value = text.trim();
  if (!value || values.some(v => sameName(v, value))) return values;
  return [...values, value];
};

export const removeChipValue = (values: string[], index: number): string[] =>
  values.filter((_, i) => i !== index);

const PROFILE_URL = /^(https?:\/\/)?([a-z]{2,3}\.)?linkedin\.com\/in\/[^\s/?#]+/i;

/** Ist der Text die URL eines LinkedIn-Profils (`…/in/<name>`)? */
export const isProfileUrl = (text: string): boolean => PROFILE_URL.test(text.trim());

export type AddResult = { values: string[]; error?: 'invalidUrl' };

/** Person zur Kampagnen-Blacklist: nur LinkedIn-Profil-URLs, Duplikate ignoriert. */
export const addPerson = (people: string[], text: string): AddResult => {
  const value = text.trim();
  if (!value) return { values: people };
  if (!isProfileUrl(value)) return { values: people, error: 'invalidUrl' };
  const url = /^https?:\/\//i.test(value) ? value : `https://${value}`;
  if (people.some(p => profileLabel(p).toLowerCase() === profileLabel(url).toLowerCase()))
    return { values: people };
  return { values: [...people, url] };
};

/** Ist die globale Liste für diese Kampagne aktiv? Ohne Eintrag gilt der Standard der Liste. */
export const isListOn = (
  state: Pick<WizardState, 'globalBlacklists'>,
  list: WizardBlacklistList
): boolean => state.globalBlacklists[list.id] ?? list.defaultOn;

/** Einträge, die im Zustand noch fehlen (neue Liste): mit dem Standard der Liste nachtragen. */
export const missingListDefaults = (
  current: Record<string, boolean>,
  lists: WizardBlacklistList[]
): Record<string, boolean> | null => {
  const missing = lists.filter(l => current[l.id] === undefined);
  if (missing.length === 0) return null;
  return { ...current, ...Object.fromEntries(missing.map(l => [l.id, l.defaultOn])) };
};

/**
 * Auswahl nach dem Anlegen einer Blacklist: neue Liste an, Einträge für alle anderen Listen
 * ergänzt (Standard der Liste), bestehende Entscheidungen bleiben.
 */
export const withNewList = (
  current: Record<string, boolean>,
  lists: WizardBlacklistList[],
  created: WizardBlacklistList | undefined
): Record<string, boolean> => {
  const next = missingListDefaults(current, lists) ?? current;
  return created ? { ...next, [created.id]: true } : next;
};

/** „148 Firmen“ / „312 Kontakte“, bei genau 1 im Singular („1 Firma“ / „1 Kontakt“). */
export const listCountLabel = (
  list: WizardBlacklistList,
  format: (n: number) => string,
  company: string,
  contact: string,
  companySingular: string = company,
  contactSingular: string = contact
): string => {
  const company_ = list.count === 1 ? companySingular : company;
  const contact_ = list.count === 1 ? contactSingular : contact;
  return `${format(list.count)} ${list.type === 'company' ? company_ : contact_}`;
};

/** Spalte 1 der Datei als Einträge: Leerzeilen weg, Kopfzeile („Name“, „Firma“ …) überspringen. */
export const extractEntries = (rows: string[][]): string[] => {
  const cells = rows
    .map(r => String(r.find(c => String(c ?? '').trim() !== '') ?? '').trim())
    .filter(Boolean);
  const header =
    /^(name|firma|firmenname|company|unternehmen|url|linkedin|linkedin-url|profil|profile|kontakt)$/i;
  const body = cells.length > 0 && header.test(cells[0]) ? cells.slice(1) : cells;
  return Array.from(new Set(body));
};

/** Name der importierten Liste aus dem Dateinamen. */
export const listNameFromFile = (filename: string): string =>
  filename.replace(/\.[^.]+$/, '').trim();

/** Zählt Einträge, die wie Profil-URLs aussehen — Vorschlag für den Typ „Kontakte“. */
export const looksLikeContacts = (entries: string[]): boolean =>
  entries.length > 0 &&
  entries.filter(e => /linkedin\.com\/in\//i.test(e)).length >= entries.length / 2;

/** Hinweis unter den Orten: der Radius-Teil („Radius anklicken“) nur bei Konten, die einen Radius können. */
export const locationHintKey = (input: {
  locMode: 'city' | 'postal';
  postalSupported: boolean;
  radiusSupported: boolean;
}): 'wizCandPostalHint' | 'wizCandLocationHint' | 'wizCandLocationHintPlain' => {
  if (input.locMode === 'postal' && input.postalSupported) return 'wizCandPostalHint';
  return input.radiusSupported ? 'wizCandLocationHint' : 'wizCandLocationHintPlain';
};
